import React, { useEffect, useMemo, useState } from 'react';
import {
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from '@material-ui/core';
import MoreVertIcon from '@material-ui/icons/MoreVert';

import { TableBodySkeleton } from '../../../shared/components/Tables/TableBodySkeleton.component';
import { useDownloadInvoice } from '../../hooks/download-invoice.hook';
import { useListInvoices } from '../../hooks/list-invoices.hook';
import { useUpdateInvoiceStatus } from '../../hooks/update-invoice-status.hook';
import { InvoiceStatus } from '../../types/invoice-status.type';
import { InvoiceType } from '../../types/invoice-type.type';
import { Invoice } from '../../types/invoice.type';
import { mapInvoiceToTableRow } from '../../utils/map-invoice-to-table-row.util';

export type InvoiceTableHeader = {
  key: string;
  label?: string;
  minWidth: number;
  align: 'left' | 'right' | 'center';
};

export type InvoiceTableRow = {
  id: string;
  status: InvoiceStatus;
  cells: { key: string; content: React.ReactNode }[];
};

type Props = {
  headers: InvoiceTableHeader[];
  listParams: { segmentId?: string; organizationId?: string; type?: InvoiceType };
  refreshSignal?: number;
};

function InvoiceTableHeaderRow({ headers }: { headers: InvoiceTableHeader[] }) {
  return (
    <TableHead>
      <TableRow>
        {headers.map((header) => (
          <TableCell
            key={header.key}
            align={header.align}
            style={{ minWidth: header.minWidth, fontWeight: 'bold' }}
          >
            {header.label ?? ''}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

function InvoiceTableEmptyBody({ headers }: { headers: InvoiceTableHeader[] }) {
  return (
    <TableBody>
      <TableRow>
        <TableCell colSpan={headers.length} style={{ textAlign: 'center' }}>
          Aucune facture
        </TableCell>
      </TableRow>
    </TableBody>
  );
}

function InvoiceTableRowComponent({
  row,
  headers,
  onDownload,
  isDownloading,
}: {
  row: InvoiceTableRow;
  headers: InvoiceTableHeader[];
  onDownload: (invoiceId: string) => void;
  isDownloading: boolean;
}) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const actionsHeader = headers[headers.length - 1];

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  return (
    <TableRow hover>
      {row.cells.map((cell, index) => (
        <TableCell
          key={cell.key}
          align={headers[index].align}
          style={{ minWidth: headers[index].minWidth }}
        >
          {cell.content}
        </TableCell>
      ))}
      <TableCell align="right" style={{ minWidth: actionsHeader.minWidth }}>
        <IconButton size="small" onClick={handleOpen}>
          <MoreVertIcon />
        </IconButton>
        <Menu
          id={`invoice-actions-${row.id}`}
          anchorEl={anchorEl}
          keepMounted
          open={Boolean(anchorEl)}
          onClose={handleClose}
        >
          <MenuItem
            disabled={isDownloading}
            onClick={() => {
              onDownload(row.id);
              handleClose();
            }}
          >
            Télécharger
          </MenuItem>
        </Menu>
      </TableCell>
    </TableRow>
  );
}

function InvoiceTableBodyRows({
  rows,
  headers,
  onDownload,
  isDownloading,
}: {
  rows: InvoiceTableRow[];
  headers: InvoiceTableHeader[];
  onDownload: (invoiceId: string) => void;
  isDownloading: boolean;
}) {
  return (
    <TableBody>
      {rows.map((row) => (
        <InvoiceTableRowComponent
          key={row.id}
          row={row}
          headers={headers}
          onDownload={onDownload}
          isDownloading={isDownloading}
        />
      ))}
    </TableBody>
  );
}

export function InvoiceListTable({ headers, listParams, refreshSignal }: Props) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  const { data, isLoading, refetch } = useListInvoices({
    ...listParams,
    page: page + 1,
    limit: rowsPerPage,
  });
  const { download, isLoading: isDownloading } = useDownloadInvoice();
  const { updateStatus } = useUpdateInvoiceStatus();

  const [localInvoices, setLocalInvoices] = useState<Invoice[]>(data.invoices);

  useEffect(() => {
    setLocalInvoices(data.invoices);
  }, [data.invoices]);

  useEffect(() => {
    refetch();
  }, [refreshSignal]);

  const handleChangeStatus = async (invoiceId: string, status: InvoiceStatus) => {
    const updated = await updateStatus(invoiceId, status);
    if (updated) {
      setLocalInvoices((prev) => prev.map((invoice) => (invoice.id === invoiceId ? updated : invoice)));
    }
  };

  const tableRows = useMemo(
    () => localInvoices.map((invoice) => mapInvoiceToTableRow(invoice, { onChangeStatus: handleChangeStatus })),
    [localInvoices],
  );

  return (
    <Paper>
      <TableContainer style={{ maxHeight: '60svh' }}>
        <Table stickyHeader>
          <InvoiceTableHeaderRow headers={headers} />
          {isLoading && (
            <TableBodySkeleton rows={rowsPerPage} columns={headers.length} />
          )}
          {!isLoading && tableRows.length === 0 && (
            <InvoiceTableEmptyBody headers={headers} />
          )}
          {!isLoading && tableRows.length > 0 && (
            <InvoiceTableBodyRows
              rows={tableRows}
              headers={headers}
              onDownload={download}
              isDownloading={isDownloading}
            />
          )}
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        rowsPerPageOptions={[50, 100, 200]}
        count={data.totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(parseInt(event.target.value, 10));
          setPage(0);
        }}
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count}`}
      />
    </Paper>
  );
}
